import Select, { components, MultiValue, MultiValueProps } from 'react-select';
import { useEffect, useState } from 'react';

import api from 'services/api';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import formatCurrency from './formatCurrency';

interface IMaterialOption {
  value: number;
  label: string;
  custo?: number;
  partNumber?: string;
  descricaoComplementar?: string;
}

interface IMaterial {
  id: number;
  codigoSap: string | null;
  diteCodigo: string;
  descricao: string;
  custoImp: number | null | undefined;
  partNumber?: string;
  descricaoComplementar?: string;
}

interface Props {
  value: number | number[] | null;
  onChange: (value: number | number[] | null) => void;
  isMulti?: boolean;

  label?: string;
  placeholder?: string;
  mostrarCusto: boolean;
}

function converterMaterial(
  material: IMaterial,
  mostrarCusto: boolean,
): IMaterialOption {
  const codigo = material.codigoSap ?? material.diteCodigo;

  return {
    value: material.id,
    label: `${codigo} - ${material.descricao}`,
    custo: mostrarCusto ? material.custoImp ?? 0 : undefined,
    partNumber: material.partNumber ?? '',
    descricaoComplementar: material.descricaoComplementar ?? '',
  };
}

export default function SelectMateriais({
  value,
  onChange,
  isMulti = false,
  label = 'Pesquise um material...',
  placeholder = 'Pesquise um material...',
  mostrarCusto,
}: Props): JSX.Element {
  const [selectedOptions, setSelectedOptions] = useState<IMaterialOption[]>([]);
  const [options, setOptions] = useState<IMaterialOption[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const carregarMateriais = async (texto: string) => {
    const textoBusca = texto.trim().replace(/\*/g, '%');

    if (textoBusca.length < 2) {
      return;
    }

    try {
      const { data } = await api.get<IMaterial[]>('/material/lista-filtrada', {
        params: {
          inputUsuario: textoBusca,
          quantidadeRegistros: 30,
        },
      });

      setOptions(
        data.map(material => converterMaterial(material, mostrarCusto)),
      );
    } catch (error) {
      console.error(error);
      setOptions([]);
    }
  };

  useDebouncedEffect(
    () => {
      carregarMateriais(inputValue);
    },
    400,
    [inputValue],
  );

  useEffect(() => {
    const carregarSelecionados = async () => {
      // eslint-disable-next-line no-nested-ternary
      const ids = isMulti
        ? (value as number[] | null) ?? []
        : value != null
        ? [value as number]
        : [];

      if (!ids.length) {
        setSelectedOptions([]);
        return;
      }

      try {
        const materiais = await Promise.all(
          ids.map(async id => {
            const { data } = await api.get(`/material/${id}`);

            return converterMaterial(data, mostrarCusto);
          }),
        );

        setSelectedOptions(materiais);
      } catch (error) {
        console.error(error);
        setSelectedOptions([]);
      }
    };

    carregarSelecionados();
  }, [value, isMulti]);

  const MultiValueCustom = (props: MultiValueProps<IMaterialOption>) => {
    // eslint-disable-next-line react/destructuring-assignment
    const values = props.selectProps.value as IMaterialOption[];

    if (menuIsOpen) {
      return <components.MultiValue {...props} />;
    }

    const index = values.findIndex(item => item.value === props.data.value);

    if (index === 0) {
      return (
        <components.MultiValue {...props}>
          {props.data.label}

          {values.length > 1 && ` + ${values.length - 1}`}
        </components.MultiValue>
      );
    }

    return null;
  };

  const handleChange = (
    value2: MultiValue<IMaterialOption> | IMaterialOption | null,
  ) => {
    if (isMulti) {
      const optionsSelecionadas = (value2 ?? []) as IMaterialOption[];

      const ids = optionsSelecionadas.map(item => item.value);

      onChange(ids);

      setSelectedOptions(optionsSelecionadas);

      // Limpa a pesquisa depois de selecionar
      setInputValue('');

      return;
    }

    const option = value2 as IMaterialOption | null;

    onChange(option?.value ?? null);

    setSelectedOptions(option ? [option] : []);
    setInputValue('');
  };

  return (
    <Select<IMaterialOption, boolean>
      placeholder={placeholder}
      options={options}
      filterOption={() => true}
      formatOptionLabel={(option, { context }) => (
        <div title={option.descricaoComplementar ?? ``}>
          <div>{option.label}</div>

          {context === 'menu' && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              {option.custo !== undefined && (
                <span>Custo: {formatCurrency(option.custo, 2)}</span>
              )}

              {option.partNumber && (
                <span style={{ marginLeft: 8 }}>
                  Part number: {option.partNumber}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      value={isMulti ? selectedOptions : selectedOptions[0] ?? null}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      blurInputOnSelect={false}
      onChange={handleChange}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      inputValue={inputValue}
      onInputChange={(value2, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(value2);
        }
      }}
      components={{
        MultiValue: MultiValueCustom,
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="bottom"
      maxMenuHeight={300}
      styles={{
        control: (base: any) => ({
          ...base,
          minHeight: 46,
        }),

        valueContainer: (base: any) => ({
          ...base,
          maxHeight: menuIsOpen ? undefined : 36,
          overflow: menuIsOpen ? 'visible' : 'hidden',
        }),

        menuPortal: (base: any) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
}
